import React, { useState, useEffect } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  IconButton,
  Tooltip,
  Pagination,
  Stack
} from '@mui/material'
import { 
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccountBalanceWallet as WalletIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material'
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore'
import { db } from '../../firebase'

interface Wallet {
  id: string
  userId: string
  userName: string
  userEmail: string
  balance: number
  currency: string
  status: 'active' | 'suspended' | 'pending'
  createdAt: any
  lastTransaction: any
}

interface Transaction {
  id: string
  walletId: string
  type: 'credit' | 'debit'
  amount: number
  description: string
  status: 'completed' | 'pending' | 'failed'
  createdAt: any
}

export default function WalletsPage() {
  const [wallets, setWallets] = useState<Wallet[]>([])
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [loading, setLoading] = useState(true)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null)
  const [transactionDialog, setTransactionDialog] = useState(false)
  const [transactionAmount, setTransactionAmount] = useState('')
  const [transactionDescription, setTransactionDescription] = useState('')
  const [transactionType, setTransactionType] = useState<'credit' | 'debit'>('credit')
  const [page, setPage] = useState(1)
  const [pageSize] = useState(20)

  useEffect(() => {
    loadWallets()
    loadTransactions()
  }, [])

  const loadWallets = async () => {
    try {
      const walletsSnapshot = await getDocs(collection(db, 'wallets'))
      const walletsData = await Promise.all(
        walletsSnapshot.docs.map(async (doc) => {
          const walletData = doc.data()
          // Get user info
          const userSnapshot = await getDocs(collection(db, 'users'))
          const user = userSnapshot.docs.find(u => u.id === walletData.userId)
          return {
            id: doc.id,
            userId: walletData.userId,
            userName: user?.data()?.name || 'غير معروف',
            userEmail: user?.data()?.email || 'غير معروف',
            balance: walletData.balance || 0,
            currency: walletData.currency || 'SAR',
            status: walletData.status || 'active',
            createdAt: walletData.createdAt,
            lastTransaction: walletData.lastTransaction
          }
        })
      )
      setWallets(walletsData)
    } catch (error) {
      console.error('Error loading wallets:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadTransactions = async () => {
    try {
      const transactionsSnapshot = await getDocs(
        query(collection(db, 'transactions'), orderBy('createdAt', 'desc'))
      )
      const transactionsData = transactionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Transaction[]
      setTransactions(transactionsData)
    } catch (error) {
      console.error('Error loading transactions:', error)
    }
  }

  const handleAddTransaction = async () => {
    if (!selectedWallet || !transactionAmount) return

    try {
      const amount = parseFloat(transactionAmount)
      const newBalance = transactionType === 'credit' 
        ? selectedWallet.balance + amount 
        : selectedWallet.balance - amount

      // Update wallet balance
      await updateDoc(doc(db, 'wallets', selectedWallet.id), {
        balance: newBalance,
        lastTransaction: new Date()
      })

      // Add transaction record
      await addDoc(collection(db, 'transactions'), {
        walletId: selectedWallet.id,
        type: transactionType,
        amount: amount,
        description: transactionDescription,
        status: 'completed',
        createdAt: new Date()
      })

      setTransactionDialog(false)
      setTransactionAmount('')
      setTransactionDescription('')
      loadWallets()
      loadTransactions()
    } catch (error) {
      console.error('Error adding transaction:', error)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success'
      case 'suspended': return 'error'
      case 'pending': return 'warning'
      default: return 'default'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'نشط'
      case 'suspended': return 'معلق'
      case 'pending': return 'في الانتظار'
      default: return 'غير معروف'
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>جاري التحميل...</Typography>
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          إدارة المحافظ
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
        >
          إضافة محفظة
        </Button>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <WalletIcon color="primary" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    إجمالي المحافظ
                  </Typography>
                  <Typography variant="h4">
                    {wallets.length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="success" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    إجمالي الأرصدة
                  </Typography>
                  <Typography variant="h4">
                    {wallets.reduce((sum, wallet) => sum + wallet.balance, 0).toFixed(2)} ريال
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingUpIcon color="info" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    المحافظ النشطة
                  </Typography>
                  <Typography variant="h4">
                    {wallets.filter(w => w.status === 'active').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <TrendingDownIcon color="warning" sx={{ mr: 2 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    المحافظ المعلقة
                  </Typography>
                  <Typography variant="h4">
                    {wallets.filter(w => w.status === 'suspended').length}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Wallets Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            قائمة المحافظ
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>المستخدم</TableCell>
                  <TableCell>البريد الإلكتروني</TableCell>
                  <TableCell>الرصيد</TableCell>
                  <TableCell>الحالة</TableCell>
                  <TableCell>آخر معاملة</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {wallets.slice((page - 1) * pageSize, page * pageSize).map((wallet) => (
                  <TableRow key={wallet.id}>
                    <TableCell>{wallet.userName}</TableCell>
                    <TableCell>{wallet.userEmail}</TableCell>
                    <TableCell>
                      <Typography variant="h6" color="primary">
                        {wallet.balance.toFixed(2)} {wallet.currency}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(wallet.status)}
                        color={getStatusColor(wallet.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {wallet.lastTransaction 
                        ? new Date(wallet.lastTransaction.seconds * 1000).toLocaleDateString('ar-SA')
                        : 'لا توجد'
                      }
                    </TableCell>
                    <TableCell>
                      <Tooltip title="إضافة معاملة">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedWallet(wallet)
                            setTransactionDialog(true)
                          }}
                        >
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="تعديل">
                        <IconButton size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {Math.ceil(wallets.length / pageSize) > 1 && (
            <Stack direction="row" justifyContent="center" sx={{ mt: 3 }}>
              <Pagination
                count={Math.ceil(wallets.length / pageSize)}
                page={page}
                onChange={(_, newPage) => setPage(newPage)}
                color="primary"
                size="large"
              />
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Transaction Dialog */}
      <Dialog open={transactionDialog} onClose={() => setTransactionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>إضافة معاملة جديدة</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              المحفظة: {selectedWallet?.userName} ({selectedWallet?.userEmail})
            </Typography>
            <Typography variant="subtitle2" gutterBottom>
              الرصيد الحالي: {selectedWallet?.balance.toFixed(2)} ريال
            </Typography>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="نوع المعاملة"
                select
                value={transactionType}
                onChange={(e) => setTransactionType(e.target.value as 'credit' | 'debit')}
                sx={{ mb: 2 }}
                SelectProps={{ native: true }}
              >
                <option value="credit">إضافة رصيد</option>
                <option value="debit">خصم رصيد</option>
              </TextField>
              <TextField
                fullWidth
                label="المبلغ"
                type="number"
                value={transactionAmount}
                onChange={(e) => setTransactionAmount(e.target.value)}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="وصف المعاملة"
                multiline
                rows={3}
                value={transactionDescription}
                onChange={(e) => setTransactionDescription(e.target.value)}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransactionDialog(false)}>إلغاء</Button>
          <Button onClick={handleAddTransaction} variant="contained">
            إضافة المعاملة
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}



















































